import { defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/group';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filterListToOptions, IdProps, sequence, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useGroupForm } from './useGroupForm';
import { useGroupRecord } from './useGroupRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const GroupEditPage = defineComponent<IdProps>({
  name: 'GroupEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('groups')) {
      return renderResourceTeaserPage(t('common.groups'));
    }

    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const userOptions = ref<SelectOptions>([]);

    // create empty record
    const record = useGroupRecord();

    // get record
    const getRecord = useTask(
      () => sequence(get(props.id), getAllUsers()),
      ([{ data, version }, users]) => {
        userOptions.value = filterListToOptions(users);
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );

    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => toastAndRedirect(t('group.updated'), 'administration', { [queryTabName]: AdministrationTabNames.GROUP }),
    );

    // form
    const { formFields, handleSubmit } = useGroupForm(record, sendRecord);

    useCreatableUserModal(userOptions, id => {
      record.value.users.push(id);
    });

    return function render() {
      return container(
        [
          pageTitle(t('group.edit')),
          box([loader('form', getRecord, [
            ...formFields(userOptions.value),
            loadingButton(
              t('group.update'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
